/** Whole rupees with Indian digit grouping: 1234567 -> "₹12,34,567". */
export function formatRupees(amount: number | null | undefined): string {
  const n = Number(amount ?? 0);
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n)}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-01" -> "Oct 2026" (no timezone maths: the date is a calendar date, not an instant). */
export function formatMonthYear(ymd: string | null | undefined): string {
  if (!ymd || !/^\d{4}-\d{2}/.test(ymd)) return "—";
  return `${MONTHS[Number(ymd.slice(5, 7)) - 1]} ${ymd.slice(0, 4)}`;
}

export type DueChanges = {
  create: number;
  update: number;
  cancel: number;
  paidUntouched: number;
  conflicts: Array<{ message: string }>;
};

/** One sentence describing what a concession change did to the student's dues. */
export function summarizeDueChanges(changes: DueChanges | undefined): string {
  if (!changes) return "";
  const parts: string[] = [];
  if (changes.update)
    parts.push(`${changes.update} ${changes.update === 1 ? "due" : "dues"} updated`);
  if (changes.create) parts.push(`${changes.create} added`);
  if (changes.cancel) parts.push(`${changes.cancel} cancelled`);
  const base = parts.length ? `${parts.join(", ")}.` : "No dues needed changing.";
  const extra: string[] = [];
  if (changes.paidUntouched) {
    extra.push(
      `${changes.paidUntouched} already paid ${changes.paidUntouched === 1 ? "due was" : "dues were"} left unchanged.`
    );
  }
  if (changes.conflicts?.length) {
    extra.push(`${changes.conflicts.length} could not be lowered below what was paid.`);
  }
  return [base, ...extra].join(" ");
}

export type ConcessionLike = {
  type: "FIXED" | "PERCENT";
  value: number;
  feeHeadName: string | null;
  effectiveFrom: string;
};

/** "20% off all fees from Oct 2026" / "₹100 off Tuition from Jun 2026". */
export function describeConcession(c: ConcessionLike): string {
  const amount = c.type === "PERCENT" ? `${c.value}%` : formatRupees(c.value);
  const scope = c.feeHeadName ?? "all fees";
  const per = c.type === "FIXED" ? " each time" : "";
  return `${amount} off ${scope}${per} from ${formatMonthYear(c.effectiveFrom)}`;
}
